
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types/auth';
import { Loader2 } from 'lucide-react';
import { useRedirect } from '@/contexts/AuthContext';

type ProtectedRouteProps = {
  allowedRoles?: UserRole[];
  redirectPath?: string;
  children?: React.ReactNode;
};

const ProtectedRoute = ({ 
  allowedRoles, 
  redirectPath = '/auth',
  children 
}: ProtectedRouteProps) => {
  const { user, loading, userRole } = useAuth();
  const location = useLocation();
  const { setRedirectPath } = useRedirect();
  const [hasInitialized, setHasInitialized] = useState(false);
  
  useEffect(() => {
    // Prevent redirect until we're sure about authentication status
    if (!loading) {
      setHasInitialized(true);
    }
  }, [loading]);

  useEffect(() => {
    // Log authentication state for debugging
    console.log('ProtectedRoute auth state:', { 
      isAuthenticated: !!user, 
      userRole, 
      allowedRoles, 
      path: location.pathname,
      loading,
      hasInitialized
    });
  }, [user, userRole, allowedRoles, location.pathname, loading, hasInitialized]);

  if (loading || !hasInitialized) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
          <p className="text-muted-foreground">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login and save current path
  if (!user) {
    console.log('Redirecting to auth: No user authenticated');
    setRedirectPath(location.pathname);
    return <Navigate to={redirectPath} state={{ from: location.pathname }} replace />;
  }

  // Role not allowed - redirect to home
  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    console.log(`Access denied: User role ${userRole} not in allowed roles`);
    return <Navigate to="/" replace />;
  }

  // User is authenticated and has the required role
  return <>{children ? children : <Outlet />}</>;
};

export default ProtectedRoute;
